/**
 * Register Page
 *
 * Immersive game-themed registration experience with pixel-art elements.
 * User registration interface with email/password authentication.
 */

import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { checkEmailCanRegister } from '../services/betaService';
import styles from './AuthPages.module.css';

/**
 * Floating pixel decoration component
 */
function PixelDecoration() {
  return (
    <div className={styles.pixelDecoration}>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
    </div>
  );
}


/**
 * Register Page Component
 */
export function RegisterPage() {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Beta whitelist state
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string>('');

  const { signUp, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/home', { replace: true });
    }
  }, [user, navigate]);

  /**
   * Validate email format
   */
  function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Check if email is approved for beta access
   */
  async function handleEmailBlur() {
    const trimmedEmail = email.trim();

    // Skip if empty or invalid format
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setEmailVerified(null);
      setEmailStatus('');
      return;
    }

    setEmailVerifying(true);
    setEmailStatus('');

    try {
      const result = await checkEmailCanRegister(trimmedEmail);

      if (result.canRegister) {
        setEmailVerified(true);
        setEmailStatus(
          result.reason === 'whitelisted'
            ? "You're approved! Create your account below."
            : 'Welcome back! You can update your account.'
        );
      } else {
        setEmailVerified(false);
        setEmailStatus('Beta access is invite-only. Request access to join the beta!');
      }
    } catch {
      setEmailVerified(false);
      setEmailStatus('Unable to verify beta access. Please try again.');
    } finally {
      setEmailVerifying(false);
    }
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    // Validation
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check beta whitelist
    if (emailVerified === false) {
      setError('Beta access is invite-only. Please request access first.');
      return;
    }

    if (emailVerified === null) {
      // Verify email if not already checked
      await handleEmailBlur();
      if (!emailVerified) {
        setError('Please verify your email is approved for beta access.');
        return;
      }
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Attempt registration
    setLoading(true);

    try {
      await signUp(email, password, displayName.trim());
      // Mark that user has an account for future visits
      localStorage.setItem('has_account', 'true');
      // Navigation happens in useEffect when user state updates
    } catch (err: any) {
      // Handle Firebase auth errors
      if (err.code === 'auth/email-already-in-use') {
        setError('Email is already in use');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.pageContainer}>
      {/* Floating pixel decorations */}
      <PixelDecoration />

      {/* Top - Banner with logo */}
      <div className={styles.bannerSection}>
        <img
          src="/assets/branding/logo-full.jpeg"
          alt="Drawn of War"
          className={styles.bannerLogo}
        />
      </div>

      {/* Main content - Form on left, Image on right */}
      <div className={styles.mainContent}>
        {/* Left - Registration form */}
        <div className={styles.formSection}>
          <div className={styles.authCard}>
            {/* Page title */}
            <h1 className={styles.pageTitle}>Join the Battle</h1>

            {/* Registration form */}
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Error message */}
              {error && (
                <div className={styles.errorMessage} role="alert">
                  {error}
                </div>
              )}

              {/* Display name field */}
              <div className={styles.formGroup}>
                <label htmlFor="displayName" className={styles.label}>
                  Warrior Name
                </label>
                <input
                  id="displayName"
                  type="text"
                  autoComplete="username"
                  placeholder="Choose your battle name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={loading}
                  className={styles.input}
                />
              </div>

              {/* Email field */}
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="warrior@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    // Reset verification when email changes
                    setEmailVerified(null);
                    setEmailStatus('');
                  }}
                  onBlur={handleEmailBlur}
                  disabled={loading || emailVerifying}
                  className={styles.input}
                />
                {/* Beta access status message */}
                {emailVerifying && (
                  <p className={styles.infoMessage}>Checking beta access...</p>
                )}
                {emailStatus && (
                  <p className={emailVerified ? styles.successMessage : styles.warningMessage}>
                    {emailStatus}
                    {!emailVerified && (
                      <>
                        {' '}
                        <Link to="/welcome" className={styles.footerLink}>
                          Request access
                        </Link>
                      </>
                    )}
                  </p>
                )}
              </div>

              {/* Password field */}
              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.label}>
                  Password
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className={styles.input}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm password field */}
              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword" className={styles.label}>
                  Confirm Password
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    className={styles.input}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className={`${styles.submitButton} ${loading ? styles.loading : ''}`}
              >
                {loading ? 'Forging Your Legend...' : 'Begin Your Journey'}
              </button>
            </form>

            {/* Footer link */}
            <div className={styles.footer}>
              <p className={styles.footerText}>
                Already a warrior?{' '}
                <Link to="/login" className={styles.footerLink}>
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right - Large emerging creature image with rising pixels */}
        <div className={styles.imageSection}>
          <img
            src="/assets/UI/emergencetheme.jpeg"
            alt="Emerging creature"
            className={styles.bottomImage}
          />
          {/* Rising white pixels */}
          <div className={styles.risingPixelsBottom}>
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
          </div>
        </div>
      </div>
    </div>
  );
}
